import { call } from '@advanza/api'
import { Col, Row } from '@advanza/grid'
import Modal from '@advanza/modal'
import { changeEntity } from '@advanza/redux_entity'
import { Button, Divider, HoverText, Icon } from '@advanza/ui'
import { fetchNotes } from 'actions/notes'
import Card from 'components/Card'
import AutoRow from 'components/misc/AutoRow'
import { useAppDispatch } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

const HUBSPOT_ID_TEST = '147556831'
const HUBSPOT_ID_PROD = '25911028'
const HUBSPOT_ACCOUNT_ID = window.location.hostname.startsWith('test')
    ? HUBSPOT_ID_TEST
    : HUBSPOT_ID_PROD

interface ButtonConfig {
    event: string
    title: string
    icon: string
    help: string
    isOpen?: boolean
    setIsOpen?: (value: boolean) => void
}

const useHubspot = (providerId: number) => {
    const dispatch = useAppDispatch()
    const [isFetching, setIsFetching] = useState(false)
    const [isError, setIsError] = useState<unknown>(false)
    const [isComplete, setIsComplete] = useState<unknown>(false)
    const { provider } = useProvider(providerId)

    const sendEvent = (event: string, button?: ButtonConfig) => {
        const endPoint = 'hubspot-profile-event'
        setIsFetching(true)
        call('office/providers/' + endPoint + '/' + providerId + '/' + event).then(
            (response) => {
                if (event === 'ob_done' || event === 'ob_missed') {
                    dispatch({ type: 'INVALIDATE_NOTES' })
                }

                if (event === 'ob_done') {
                    if (response.addedOnboardingOwnerLabel && provider) {
                        const providerWithLabels = provider as typeof provider & {
                            office_labels: unknown[]
                        }
                        dispatch(
                            changeEntity({
                                store: 'providers',
                                name: 'providers',
                                key: providerId,
                                diff: {
                                    office_labels: providerWithLabels.office_labels.concat(
                                        response.addedOnboardingOwnerLabel,
                                    ),
                                },
                            }),
                        )
                    }
                }

                setIsFetching(false)
                setIsComplete(response)

                if (button?.setIsOpen) {
                    button.setIsOpen(false)
                }
            },
            (reason) => {
                setIsFetching(false)
                setIsError(reason)
            },
        )
    }

    return {
        isFetching,
        isError,
        isComplete,
        sendEvent,
        provider,
    }
}

interface Props {
    providerId: number
}

const ProviderHubspot = ({ providerId }: Props) => {
    const dispatch = useAppDispatch()
    const notesState = useSelector((state: { notes: { didInvalidate: boolean } }) => state.notes)

    const { isFetching, isError, isComplete, sendEvent, provider } = useHubspot(providerId)
    const isClicked = isFetching || !!isError || !!isComplete

    useEffect(() => {
        if (notesState.didInvalidate) {
            dispatch(fetchNotes(providerId))
        }
    }, [notesState.didInvalidate, dispatch, providerId])

    const buttons: Record<string, ButtonConfig> = {
        disable_profile_send_mail: {
            event: 'disable_profile_send_mail',
            title: 'Confirm disable profile',
            icon: 'power_off',
            help: `Set disabled status and send confirmation email.`,
        },
        silent_disable: {
            event: 'silent_disable',
            title: 'Confirm silent_disable',
            icon: 'power_off',
            help: `Set disabled status, no email.`,
        },
        ob_done: {
            event: 'ob_done',
            title: 'Confirm onboarding done',
            icon: 'flag',
            help: `Set changes event field to: "OB done". Triggers after OB email with signature account owner.`,
        },
        ob_missed: {
            event: 'ob_missed',
            title: 'Confirm onboarding missed',
            icon: 'mail',
            help: `Set changes event field to: "OB missed". Triggers OB missed mailflow with signature account owner.`,
        },
        ob2_start: {
            event: 'ob2_start',
            title: 'OB2 Start',
            icon: 'mail',
            help: `Start OB2 process`,
        },
        force_update: {
            event: 'force_update',
            title: 'Update provider',
            icon: 'update',
            help: `Forces a provider update to HubSpot`,
        },
        force_token: {
            event: 'force_token',
            title: 'Token refresh',
            icon: 'refresh',
            help: `Forces a token refresh to HubSpot`,
        },
        force_reset: {
            event: 'force_reset',
            title: 'Force contact id reset',
            icon: 'refresh',
            help: `Forces a reset of the contactId connection this provider has with HubSpot, tries to match with Provider ID field.`,
        },
    }

    const [isOpens, setIsOpens] = useState({} as Record<string, boolean>)
    for (const button in buttons) {
        buttons[button].isOpen = isOpens[button]
        buttons[button].setIsOpen = (value: boolean) => setIsOpens({ ...isOpens, [button]: value })
    }

    const errorMessage = (() => {
        if (!isError) return null
        if (typeof isError === 'object' && isError !== null && 'error' in isError) {
            return String((isError as { error?: unknown }).error)
        }
        return String(isError)
    })()

    const successMessage = (() => {
        if (!isComplete) return null
        if (typeof isComplete === 'object' && isComplete !== null && 'status' in isComplete) {
            return String((isComplete as { status?: unknown }).status)
        }
        return String(isComplete)
    })()

    const status = (
        <>
            {isFetching && (
                <Col x>
                    <p className="">...working...</p>
                </Col>
            )}

            {errorMessage && (
                <Col x>
                    <p className="error">
                        <Icon name="error" /> Error: {errorMessage}
                    </p>
                </Col>
            )}

            {successMessage && (
                <Col x>
                    <p className="success">{successMessage}</p>
                </Col>
            )}
        </>
    )

    const buttonElement = (button: ButtonConfig, i: number = 0) => {
        return (
            <Col key={i} x>
                <HoverText
                    enableClick
                    trigger={
                        <Button
                            disabled={isClicked}
                            onClick={() => button.setIsOpen?.(true)}
                            style={{ cursor: 'help' }}>
                            <Icon name={button.icon} /> {button.event} profile
                        </Button>
                    }>
                    {button.help}
                </HoverText>
                <Divider />
                <Modal
                    close={() => button.setIsOpen?.(false)}
                    maxWidth={600}
                    open={Boolean(button.isOpen)}>
                    <div>
                        <h3>{button.title}?</h3>
                        <Divider />
                        <div>{button.help}</div>
                        <Divider />
                        <Row>
                            <Col x>
                                <Button
                                    disabled={isClicked}
                                    onClick={() => sendEvent(button.event, button)}>
                                    <Icon name={button.icon} /> {button.title}
                                </Button>
                                <Divider />
                            </Col>
                        </Row>
                        <Divider />
                        {status}
                    </div>
                </Modal>
            </Col>
        )
    }

    return (
        <div>
            <Card header="HubSpot actions" icon="double_arrow">
                <Divider m />
                {provider && 'hubspot_contact_id' in provider && provider.hubspot_contact_id && (
                    <AutoRow>
                        <a
                            target="_blank"
                            href={`https://app-eu1.hubspot.com/contacts/${HUBSPOT_ACCOUNT_ID}/contact/${provider.hubspot_contact_id}`}
                            rel="noreferrer">
                            <Icon name="device_hub" /> HS {String(provider.hubspot_contact_id)}
                        </a>
                    </AutoRow>
                )}
                <Divider m />
                <Row>
                    {[buttons.force_reset, buttons.force_update, buttons.force_token].map(
                        (button, i) => {
                            return buttonElement(button, i)
                        },
                    )}
                </Row>
                <Divider />
            </Card>
            <Divider />

            <Card header="Unsub Emails" icon="mail">
                <Divider />
                <Row>
                    {[buttons.disable_profile_send_mail, buttons.silent_disable].map(
                        (button, i) => {
                            return buttonElement(button, i)
                        },
                    )}
                </Row>
                <Divider />
            </Card>
            <Divider />

            <Card header="CS Emails" icon="mail">
                <Divider />
                <Row>
                    {[buttons.ob_done, buttons.ob_missed].map((button, i) => {
                        return buttonElement(button, i)
                    })}
                </Row>
                <Divider />
            </Card>

            <Divider />
            <Card header="OB2" icon="mail">
                <Divider />
                <Row>
                    {[buttons.ob2_start].map((button, i) => {
                        return buttonElement(button, i)
                    })}
                </Row>
                <Divider />
            </Card>

            {status}
        </div>
    )
}

export default ProviderHubspot
